import {Image} from '@primer/react-brand/esm'
import {AutoPlayVideo} from '@github-ui/swp-core/components/AutoPlayVideo'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import type {GenericAsset} from '../../../../../../brand/lib/types/contentful'
import {useProductMediaLoading} from '../Product/product-media-loading-context'

import Styles from './MediaAsset.module.css'

type Props = {
  content: GenericAsset[]
  location: string
  loading?: 'eager' | 'lazy'
}

export function isImageAsset(asset: GenericAsset): boolean {
  return Boolean(asset.fields.asset.fields.file.details?.image)
}

export function MediaAsset(props: Props) {
  const {content, location, loading = 'lazy'} = props
  const productMediaLoading = useProductMediaLoading(loading)

  // Media assets added in Contentful are shown based on the following priority:

  // 1. Whatever assets are added first in Contentful are preferred.
  // 2. If a video AND an image is available, the first video will be shown and the first image is used as the video's "poster" image.
  //    Make sure the video and poster image have the same orientation, resolution and first frame matches.

  const image = content.find(isImageAsset)?.fields
  const video = content.find(asset => !isImageAsset(asset))?.fields
  const poster = video ? image : undefined

  return (
    <div className={Styles.MediaAsset}>
      {video ? (
        <AutoPlayVideo
          className={Styles.Video}
          src={video.asset.fields.file.url}
          poster={poster?.asset.fields.file.url || ''}
          width={video.width || ''}
          height={video.height || ''}
          aria-label={video.description || ''}
          getButtonAnalyticsAttributes={action =>
            getAnalyticsEvent({action, tag: 'button', context: 'video', location})
          }
        />
      ) : image ? (
        <Image
          className={Styles.Image}
          src={image.asset.fields.file.url}
          width={image.width || image.asset.fields.file.details?.image?.width || ''}
          height={image.height || image.asset.fields.file.details?.image?.height || ''}
          alt={image.asset.fields.description || ''}
          loading={productMediaLoading}
        />
      ) : null}
    </div>
  )
}
